import React, { useState, useRef, useEffect } from "react";
import { Form, Button, Spinner, Card, Badge, Row, Col, Alert } from "react-bootstrap";
import axios from "axios";
import NovaAvatar from "../avatars-256X256/Nova.png";
import Swal from "sweetalert2";
import { FaPaperPlane, FaPaperclip, FaSearch, FaCheckCircle, FaExclamationTriangle, FaTimesCircle, FaClock } from "react-icons/fa";
import { FUNCTION_URLS } from "../../../config/functionUrls";

const NovaChat = ({ usuario }) => {
  const [messages, setMessages] = useState([
    {
      sender: 'nova',
      text: '¡Hola! Soy Nova, tu especialista en Onboarding. Para comenzar, por favor ingresa el DNI del colaborador que deseas consultar o auditar.',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [input, setInput] = useState('');
  const [dni, setDni] = useState('');
  const [loading, setLoading] = useState(false);
  const [documentos, setDocumentos] = useState([]);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (textOverride = null, fileData = null) => {
    const messageText = textOverride || input;
    if (!messageText.trim() && !fileData) return;

    // Si aún no hay DNI y el mensaje parece un DNI, lo seteamos
    let targetDni = dni;
    if (!dni && /^\d{8,9}$/.test(messageText.trim())) {
      targetDni = messageText.trim();
      setDni(targetDni);
    }

    const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    // Agregar mensaje del usuario a la UI
    if (!fileData) {
      setMessages(prev => [...prev, { sender: 'user', text: messageText, timestamp }]);
      setInput('');
    } else {
      setMessages(prev => [...prev, { sender: 'user', text: `[Archivo adjunto: ${fileData.name}]`, timestamp }]);
    }

    setLoading(true);

    try {
      const payload = {
        dni: targetDni || "INVITADO",
        message: messageText,
        imageBase64: fileData?.base64 || null,
        mimeType: fileData?.type || null
      };

      const response = await axios.post(FUNCTION_URLS.novaInteraction, payload);
      const novaData = response.data;

      // Actualizar documentos si vienen en la respuesta
      if (novaData.document_status) {
        setDocumentos(novaData.document_status);
      }

      // Agregar respuesta de Nova
      setMessages(prev => [...prev, { 
        sender: 'nova', 
        text: novaData.message, 
        actions: novaData.actions,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) 
      }]);

    } catch (error) {
      console.error("Error en Nova Chat:", error);
      setMessages(prev => [...prev, { 
        sender: 'nova', 
        text: "Lo siento, tuve un problema al procesar tu solicitud. Por favor verifica el DNI e intenta de nuevo.",
        isError: true,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!dni) {
        Swal.fire("Atención", "Por favor ingresa primero el DNI del colaborador.", "warning");
        return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result.split(',')[1];
      handleSend(`Archivo: ${file.name}`, {
        name: file.name,
        base64: base64String,
        type: file.type
      });
    };
    reader.readAsDataURL(file);
    // Limpiar input
    e.target.value = null;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'APROBADO': return <FaCheckCircle className="text-success" />;
      case 'OBSERVADO': return <FaExclamationTriangle className="text-warning" />;
      case 'INCORRECTO': return <FaTimesCircle className="text-danger" />;
      case 'PENDIENTE': return <FaClock className="text-muted" />;
      case 'VENCIDO': return <FaExclamationTriangle className="text-danger" />;
      default: return <FaClock className="text-muted" />;
    }
  };

  return (
    <div className="nova-chat-wrapper p-4" style={{ background: '#0f0f0f', minHeight: '100vh', color: '#fff' }}>
      <Row className="h-100">
        {/* Columna Izquierda: Chat */}
        <Col md={8} className="d-flex flex-column" style={{ height: '85vh' }}>
          <Card className="flex-grow-1 border-0 shadow-lg" style={{ background: '#1a1a1a', borderRadius: '20px', overflow: 'hidden' }}>
            <Card.Header className="bg-dark border-secondary p-3 d-flex align-items-center">
              <img src={NovaAvatar} alt="Nova" className="rounded-circle me-3" style={{ width: '40px', height: '40px', border: '2px solid #5e5ce6' }} />
              <div>
                <h5 className="m-0 text-white">Nova</h5>
                <small className="text-success">● Especialista en Onboarding</small>
              </div>
            </Card.Header>
            <Card.Body className="p-4" style={{ overflowY: 'auto' }}>
              {messages.map((msg, idx) => (
                <div key={idx} className={`d-flex mb-4 ${msg.sender === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                  <div className={`p-3 animate__animated animate__fadeInUp`} style={{
                    maxWidth: '80%',
                    borderRadius: msg.sender === 'user' ? '20px 20px 0 20px' : '20px 20px 20px 0',
                    background: msg.sender === 'user' ? 'linear-gradient(135deg, #5e5ce6 0%, #3634a3 100%)' : '#2c2c2e',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.3)'
                  }}>
                    <p className="m-0 mb-1" style={{ whiteSpace: 'pre-line' }}>{msg.text}</p>
                    
                    {/* Renderizar acciones si existen */}
                    {msg.actions && msg.actions.length > 0 && (
                      <div className="mt-3 d-flex flex-wrap gap-2">
                        {msg.actions.map((action, aIdx) => (
                          <Button 
                            key={aIdx} 
                            variant="outline-light" 
                            size="sm" 
                            onClick={() => handleSend(`${action.type}: ${action.documento}`)}
                            className="rounded-pill px-3"
                            style={{ fontSize: '11px', borderColor: 'rgba(255,255,255,0.2)' }}
                          >
                            {action.type.replace('_', ' ')}: {action.documento}
                          </Button>
                        ))}
                      </div>
                    )}
                    
                    <div className="text-end" style={{ fontSize: '10px', opacity: 0.6 }}>{msg.timestamp}</div>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="d-flex justify-content-start mb-3">
                  <div className="p-3 bg-secondary rounded-pill d-flex align-items-center bg-opacity-25">
                    <Spinner animation="grow" size="sm" variant="light" className="me-2" />
                    <small>Nova está analizando...</small>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </Card.Body>
            <Card.Footer className="bg-dark border-0 p-3">
              <Form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="d-flex gap-2">
                <Button 
                  variant="link" 
                  className="text-white p-0 px-2" 
                  onClick={() => fileInputRef.current.click()}
                  title="Adjuntar documento"
                >
                  <FaPaperclip size={20} />
                </Button>
                <input 
                  type="file" 
                  hidden 
                  ref={fileInputRef} 
                  onChange={handleFileChange}
                  accept="image/*,application/pdf"
                />
                <Form.Control 
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={dni ? "Escribe un mensaje..." : "Ingresa el DNI para comenzar..."}
                  className="rounded-pill border-0 px-4"
                  style={{ background: '#2c2c2e', color: '#fff' }}
                />
                <Button type="submit" variant="primary" className="rounded-circle p-0" style={{ width: '45px', height: '45px', background: '#5e5ce6' }}>
                  <FaPaperPlane />
                </Button>
              </Form>
            </Card.Footer>
          </Card>
        </Col>

        {/* Columna Derecha: Estado de Documentos */}
        <Col md={4}>
          <Card className="border-0 shadow-lg h-100" style={{ background: '#1a1a1a', borderRadius: '20px' }}>
            <Card.Header className="bg-dark border-secondary p-3">
              <h6 className="m-0 text-white d-flex align-items-center">
                <FaCheckCircle className="me-2 text-primary" />
                Estado de Onboarding
              </h6>
            </Card.Header>
            <Card.Body className="p-4 overflow-auto">
              {!dni ? (
                <div className="text-center py-5 opacity-50">
                  <FaSearch size={40} className="mb-3" />
                  <p>Ingresa un DNI en el chat para visualizar el estado documental.</p>
                </div>
              ) : (
                <>
                  <div className="mb-4 pb-3 border-bottom border-secondary bg-opacity-10 bg-info p-3 rounded">
                    <small className="text-white-50 d-block mb-1">Colaborador Auditar:</small>
                    <h5 className="text-white m-0">{dni}</h5>
                  </div>

                  <div className="doc-list">
                    {documentos.length > 0 ? documentos.map((doc, dIdx) => (
                      <div key={dIdx} className="p-3 mb-2 rounded d-flex justify-content-between align-items-center" style={{ background: '#2c2c2e', borderLeft: '3px solid' + (doc.status === 'APROBADO' ? '#4cd964' : '#ffcc00') }}>
                        <div>
                          <strong className="d-block text-white" style={{ fontSize: '14px' }}>{doc.title}</strong>
                          <small className="text-white-50">{doc.status}</small>
                        </div>
                        {getStatusIcon(doc.status)}
                      </div>
                    )) : (
                      <Alert variant="dark" className="bg-dark border-secondary text-white-50">
                        No se detectaron documentos configurados para este colaborador aún. Nova puede ayudarte a configurarlos.
                      </Alert>
                    )}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default NovaChat;
